import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Button from '../ui/Button';
import RejectDialog from '../candidates/RejectDialog';
import { pushToast } from '../ui/toastBus';

// Inline Approve / Reject buttons for one request row. Reviewed rows show nothing.
export default function RequestActions({
  pending,
  onApprove,
  onReject,
  onResolved,
  approvedMessage = 'Request approved.',
  rejectedMessage = 'Request rejected.',
  RejectModal = RejectDialog,
  children,
}) {
  const [rejectOpen, setRejectOpen] = useState(false);

  const approveMutation = useMutation({
    mutationFn: onApprove,
    onSuccess: () => {
      pushToast({ type: 'success', message: approvedMessage });
      onResolved();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not approve request.' }),
  });

  const rejectMutation = useMutation({
    mutationFn: onReject,
    onSuccess: () => {
      pushToast({ type: 'success', message: rejectedMessage });
      setRejectOpen(false);
      onResolved();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reject request.' }),
  });

  return (
    <div className="flex justify-end gap-2">
      {children}
      {pending && (
        <>
          <Button onClick={() => approveMutation.mutate()} loading={approveMutation.isPending} disabled={rejectMutation.isPending}>
            Approve
          </Button>
          <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={approveMutation.isPending}>
            Reject
          </Button>
          <RejectModal
            open={rejectOpen}
            onClose={() => setRejectOpen(false)}
            onConfirm={(reason) => rejectMutation.mutate(reason)}
            loading={rejectMutation.isPending}
            actionLabel="Reject request"
          />
        </>
      )}
    </div>
  );
}

// Leading checkbox column for useRowSelection; only pending rows get a box.
export function selectColumn(selection, getId, isSelectable) {
  return {
    key: 'select',
    label: '',
    render: (row) =>
      isSelectable(row) ? (
        <input
          type="checkbox"
          checked={selection.isSelected(getId(row))}
          onChange={() => selection.toggle(getId(row))}
          aria-label="Select request"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
      ) : null,
  };
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export function CandidateCell({ candidate }) {
  return (
    <div>
      <Link to={`/candidates/${candidate.id}`} className="font-medium text-primary hover:underline">
        {candidate.full_name}
      </Link>
      <div className="text-xs text-gray-400">{candidate.reference_no}</div>
    </div>
  );
}

export function ReviewedCell({ row }) {
  if (row.status === 'pending') return null;
  return (
    <div className="text-xs text-gray-500">
      {row.reviewed_by}
      {row.reviewed_at && <div>{fmtDate(row.reviewed_at)}</div>}
      {row.review_remarks && <div className="italic">"{row.review_remarks}"</div>}
    </div>
  );
}
